import React, { useState } from 'react';
import { Input, InputNumber, Button, Modal, Spin, Checkbox, Radio } from 'antd';
import { usePaymentTermsForm } from './hooks/usePaymentTermsForm';

const { TextArea } = Input;

const P1116Form: React.FC = () => {
  const {
    mode, isEditing, loading, records, current, currentIdx, form, isRatioMode,
    setField, setRatioField, toggleRatioMode,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save, doDelete, doCopy, quickSearch,
  } = usePaymentTermsForm();

  const [searchText, setSearchText] = useState('');
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copyCode, setCopyCode] = useState('');
  const [ratioChecked, setRatioChecked] = useState(false);

  // BR-C03: Sync ratio checkbox from form data
  const ratioSum = form.f116_21 + form.f116_22 + form.f116_23;
  const showRatioMode = isEditing ? ratioChecked : ratioSum > 0;

  const handleDelete = () => {
    if (!current) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除「${current.f116_01}」（${current.f116_11}）？`,
      okText: '刪除', okType: 'danger', cancelText: '取消', onOk: doDelete,
    });
  };

  const handleCopy = () => { setCopyCode(''); setCopyModalOpen(true); };
  const handleCopyOk = () => {
    if (!copyCode.trim()) return;
    doCopy(copyCode.trim());
    setCopyModalOpen(false);
  };

  const handleStartAdd = () => { startAdd(); setRatioChecked(false); };
  const handleStartEdit = () => {
    startEdit();
    setRatioChecked(ratioSum > 0);
  };

  const handleRatioToggle = (checked: boolean) => {
    setRatioChecked(checked);
    toggleRatioMode(checked);
  };

  const modeLabel = mode === 'add' ? '新增' : mode === 'edit' ? '修改' : '瀏覽';

  return (
    <Spin spinning={loading}>
      <div className="max-w-3xl mx-auto p-6">
        <h2 className="text-xl font-bold mb-4">
          付款條件定義 (pw1116f1)
          <span className="ml-3 text-sm font-normal text-gray-500">
            [{modeLabel}] {records.length > 0 ? `${currentIdx + 1} / ${records.length}` : '無資料'}
          </span>
        </h2>

        {/* Quick search + Navigation */}
        <div className="flex items-center gap-2 mb-4">
          <Input placeholder="代號查詢" value={searchText} onChange={e => setSearchText(e.target.value)}
            onPressEnter={() => searchText.trim() && quickSearch(searchText.trim())}
            disabled={isEditing} style={{ width: 150 }} maxLength={2} />
          <Button onClick={() => searchText.trim() && quickSearch(searchText.trim())} disabled={isEditing}>查詢</Button>
          <span className="mx-2" />
          <Button size="small" onClick={navFirst} disabled={isEditing || !records.length}>|&lt;</Button>
          <Button size="small" onClick={navPrev} disabled={isEditing || currentIdx <= 0}>&lt;</Button>
          <Button size="small" onClick={navNext} disabled={isEditing || currentIdx >= records.length - 1}>&gt;</Button>
          <Button size="small" onClick={navLast} disabled={isEditing || !records.length}>&gt;|</Button>
        </div>

        {/* Form fields */}
        <div className="space-y-4 mb-6">
          {/* F116_01 代號 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">付款條件代號 (F116_01)</label>
            <Input value={form.f116_01} onChange={e => setField('f116_01', e.target.value)}
              maxLength={2} disabled={mode !== 'add'} style={{ width: 100 }} placeholder="2碼" />
          </div>

          {/* F116_11 說明 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">條件說明 (F116_11)</label>
            <Input value={form.f116_11} onChange={e => setField('f116_11', e.target.value)}
              maxLength={80} disabled={!isEditing || showRatioMode} style={{ width: 500 }} />
          </div>

          {/* F116_12 天數 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">付款天數 (F116_12)</label>
            <InputNumber value={form.f116_12} onChange={v => setField('f116_12', v ?? 0)}
              min={0} max={999} disabled={!isEditing} style={{ width: 100 }} />
          </div>

          {/* F116_13 類別 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">付款條件類別 (F116_13)</label>
            <Radio.Group value={form.f116_13} onChange={e => setField('f116_13', e.target.value)} disabled={!isEditing}>
              <Radio value={1}>L/C</Radio>
              <Radio value={2}>T/T</Radio>
              <Radio value={3}>支票</Radio>
              <Radio value={4}>其他</Radio>
            </Radio.Group>
          </div>

          {/* 付款比例 section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">付款比例</label>
            <Checkbox checked={showRatioMode} onChange={e => handleRatioToggle(e.target.checked)}
              disabled={!isEditing}>
              使用付款比例模式
            </Checkbox>
          </div>

          {showRatioMode && (
            <div className="pl-4 space-y-3">
              <div className="flex items-center gap-4">
                <div>
                  <label className="text-xs text-gray-500">下單後預付款比例</label>
                  <InputNumber value={form.f116_21} onChange={v => setRatioField('f116_21', v ?? 0)}
                    min={0} max={100} disabled={!isEditing} style={{ width: 80 }} addonAfter="%" />
                </div>
                <div>
                  <label className="text-xs text-gray-500">出貨前付款比例</label>
                  <InputNumber value={form.f116_22} onChange={v => setRatioField('f116_22', v ?? 0)}
                    min={0} max={100} disabled={!isEditing} style={{ width: 80 }} addonAfter="%" />
                </div>
                <div>
                  <label className="text-xs text-gray-500">出貨後付款比例</label>
                  <InputNumber value={form.f116_23} onChange={v => setRatioField('f116_23', v ?? 0)}
                    min={0} max={100} disabled={!isEditing} style={{ width: 80 }} addonAfter="%" />
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-500">餘額付款天數</label>
                <InputNumber value={form.f116_24} onChange={v => setRatioField('f116_24', v ?? 0)}
                  min={0} max={999} disabled={!isEditing || (form.f116_23 === 0 && form.f116_21 + form.f116_22 === 100)}
                  style={{ width: 80 }} addonAfter="天" />
              </div>
            </div>
          )}

          {/* F116_31 詳細說明 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">詳細說明 (F116_31)</label>
            <TextArea value={form.f116_31} onChange={e => setField('f116_31', e.target.value)}
              disabled={!isEditing} rows={3} style={{ width: 500 }} />
          </div>

          {/* F116_14 舊代號 */}
          <div>
            <Checkbox checked={form.f116_14} onChange={e => setField('f116_14', e.target.checked)}
              disabled={!isEditing}>
              舊代號 (F116_14)
            </Checkbox>
          </div>
        </div>

        {/* Metadata display */}
        {current && mode === 'browse' && (
          <div className="text-xs text-gray-400 mb-6">
            儲存年月: {current.f116_91 || '-'} | 儲存日期: {current.f116_92 || '-'} | 儲存時間: {current.f116_93 || '-'}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex items-center gap-2">
          {!isEditing ? (
            <>
              <Button type="primary" onClick={handleStartAdd}>新增</Button>
              <Button onClick={handleStartEdit} disabled={!current}>修改</Button>
              <Button danger onClick={handleDelete} disabled={!current}>刪除</Button>
              <Button onClick={handleCopy} disabled={!current}>複製</Button>
            </>
          ) : (
            <>
              <Button type="primary" onClick={save}>儲存</Button>
              <Button onClick={cancel}>取消</Button>
            </>
          )}
        </div>

        {/* Copy modal */}
        <Modal title="複製付款條件" open={copyModalOpen} onOk={handleCopyOk}
          onCancel={() => setCopyModalOpen(false)} okText="確定" cancelText="取消">
          <div className="mb-2">來源代號：{current?.f116_01}</div>
          <Input placeholder="請輸入新代號" value={copyCode} onChange={e => setCopyCode(e.target.value)}
            maxLength={2} style={{ width: 100 }} />
        </Modal>
      </div>
    </Spin>
  );
};

export default P1116Form;
